'use client'

import { useEffect, useMemo } from 'react'
import Button from '@wiggui/ds/Button'
import Flex from '@wiggui/ds/Flex'
import GoogleIcon from '@wiggui/ds/GoogleIcon'
import Modal from '@wiggui/ds/Modal'
import Spinner from '@wiggui/ds/Spinnner'
import Text from '@wiggui/ds/Text'
import { PortalIcon, PortalNames } from '@wiggui/ds/PortalIcon'

import { AdOption } from './AdOption'
import { usePublishPortalErrors } from '../hooks/usePublishPortalErrors'
import { ROUTES, idToPortalName } from '../../../constants'
import { useModalPublishPortalsErrorStore } from '../../../store/modals/modal-publish-portals-error.store'
import { capitalizeFirstLetter } from '../../../utils'

export const ModalPublishErrorsProperty = () => {
  const {
    handleOnClick,
    onClose,
    setDisabledConfirmButton,
    backStep,
    disabledConfirmButton,
    isDisabledByOptionWithoutAd,
    isLoading,
    isLoadingPublish,
    modalState,
    optionsByTabAndType,
    step,
    textsByType,
    selectedPortalsIds
  } = usePublishPortalErrors()

  const portalIsChecked = useModalPublishPortalsErrorStore(
    (state) => state?.portalIsChecked
  )
  const selectedAdOptions = useModalPublishPortalsErrorStore(
    (state) => state?.selectedAdOptions
  )

  const isFirtsStep = step === 0

  const adsSelectedResume = useMemo(() => {
    const portalsSelected: {
      portalId: string
      name?: string
    }[] = []
    selectedPortalsIds.forEach((portalSelectedId) => {
      let hasOption = false
      selectedAdOptions.forEach((option) => {
        if (option.portalId === portalSelectedId) {
          portalsSelected.push({
            portalId: portalSelectedId,
            name: option.name
          })
          hasOption = true
        }
      })
      if (!hasOption) {
        portalsSelected.push({
          portalId: portalSelectedId
        })
      }
    })
    return portalsSelected
  }, [selectedPortalsIds, selectedAdOptions])

  useEffect(() => {
    let hasChecked = false
    optionsByTabAndType.forEach((option) => {
      if (option.publishIsFailed && portalIsChecked(option.portalId)) {
        hasChecked = true
      }
    })
    if (!hasChecked) {
      setDisabledConfirmButton?.(true)
    }
  }, [])

  return (
    <Modal
      className="w-full max-w-[526px] max-h-[99vh]"
      enableBgBlur
      onClose={isLoadingPublish ? () => ({}) : onClose}
      isOpen={modalState?.isOpen}
    >
      {isFirtsStep ? (
        <Modal.Header
          className="h-[64px]"
          title={textsByType?.title}
          isShowCloseButton
          isShowDivider
        />
      ) : (
        <Modal.Header className="w-full px-6 pt-3 pb-1 flex justify-between items-center border-b border-surface-strokes">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              backStep()
            }}
            className="p-3 flex justify-center items-center"
          >
            <GoogleIcon className="text-[24px]" name="chevron_left" />
          </button>
          <p className="text-lg font-semibold">Actualizar</p>
          <button
            type="button"
            disabled={isLoadingPublish}
            onClick={onClose}
            className="p-3 flex justify-center items-center"
          >
            <GoogleIcon className="text-[24px]" name="close" />
          </button>
        </Modal.Header>
      )}
      {isFirtsStep ? (
        <Modal.Body className="w-full flex gap-6 flex-col items-center justify-center">
          <Flex
            className="overflow-y-auto h-full pb-5 ui-kit-scrollbar max-h-[50vh]"
            dir="col"
            gap="gap-6"
            items="center"
            justify="start"
          >
            <Flex
              dir="col"
              gap="gap-6"
              items="center"
              justify="start"
              wFull
              className="px-6"
            >
              <Text className="text-body-m text-primary">
                {textsByType?.portalDescription}

                <a
                  href={`${ROUTES.MY_PROPERTIES}/relationships`}
                  target="_blank"
                  className="font-bold underline ml-1"
                  rel="noreferrer"
                >
                  vinculaciones.
                </a>
              </Text>
            </Flex>
            <Flex
              dir="col"
              gap="gap-6"
              items="center"
              justify="start"
              wFull
              className="px-6"
            >
              {isLoading && (
                <Flex items="center" justify="center" wFull className="py-2">
                  <Spinner color="terciary" />
                </Flex>
              )}
              {!isLoading &&
                optionsByTabAndType?.length > 0 &&
                optionsByTabAndType
                  ?.filter((option) => option?.publishIsFailed)
                  .map((option) => {
                    const wiggotTitle =
                      'Tus propiedades siempre se publicarán en Wiggot®'

                    const monopolioTitle =
                      'Tus propiedades siempre se publicarán en Monopolio®'

                    const generateDelayedText = () => {
                      if (!option?.showDelayedText) {
                        return undefined
                      }

                      return 'La publicación puede tardar en este portal.'
                    }

                    const generateDescription = () => {
                      if (option?.portalId?.toUpperCase() === 'WIGGOT') {
                        return wiggotTitle
                      }
                      if (option?.portalId?.toUpperCase() === 'MONOPOLIO') {
                        return monopolioTitle
                      }

                      return undefined
                    }

                    return (
                      <AdOption
                        publishIsFailed={option?.publishIsFailed}
                        delayedText={generateDelayedText()}
                        setDisabledConfirmButton={setDisabledConfirmButton}
                        id={option?.portalId}
                        key={option?.portalId}
                        description={generateDescription()}
                        img={option?.img}
                        options={optionsByTabAndType}
                      />
                    )
                  })}
              {!isLoading && optionsByTabAndType?.length === 0 && (
                <Text className="text-body-m font-bold text-primary">
                  No tienes portales inmobiliarios disponibles para{' '}
                  {textsByType?.type}
                </Text>
              )}
            </Flex>
          </Flex>
        </Modal.Body>
      ) : (
        <Modal.Body className="flex flex-col gap-6 p-6">
          <p className="text-sm">
            Al actualizar se realizarán los siguientes cambios en la propiedad:
            <span className="font-semibold">
              {modalState?.propertyTitle ? modalState.propertyTitle : ''}
            </span>
          </p>
          <div className="flex flex-col gap-3">
            {adsSelectedResume?.map((adOption) => (
              <div
                key={adOption.name + adOption.portalId}
                className="flex p-3 gap-4 flex-col w-full rounded-xl bg-surface-gray-10"
              >
                <div className="flex justify-between items-center">
                  <p className="text-base">Portal</p>
                  <div className="flex gap-2 items-center">
                    <PortalIcon
                      iconProps={{
                        fillColor: '#FFF',
                        width: '32px',
                        height: '32px'
                      }}
                      name={adOption.portalId.toLowerCase() as PortalNames}
                    />
                    <p className="text-base font-semibold">
                      {
                        idToPortalName[
                          adOption.portalId.toLowerCase() as PortalNames
                        ]
                      }
                    </p>
                  </div>
                </div>
                {adOption.name && (
                  <div className="flex justify-between">
                    <p className="text-base">Tipo de anuncio</p>
                    <p className="text-base font-semibold">
                      {capitalizeFirstLetter(adOption.name)}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Modal.Body>
      )}
      <Modal.Footer
        className="w-full py-[9px] px-6 flex flex-row gap-2 items-center justify-center"
        isShowDivider
      >
        <Button
          variant="terciary"
          fullWidth
          isDisabled={isLoadingPublish}
          onClick={isFirtsStep ? onClose : backStep}
        >
          {isFirtsStep ? 'Cancelar' : 'Regresar'}
        </Button>
        <Button
          variant="secondary"
          isLoading={isLoadingPublish}
          fullWidth
          isDisabled={disabledConfirmButton || isDisabledByOptionWithoutAd}
          onClick={handleOnClick}
        >
          {textsByType?.title}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
